<template>
  <div>
    <v-container>
      <div class="d-flex justify-space-between align-center mb-4">
        <h2 class="text-h5">Custom Plans</h2>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>{{ icons.mdiPlus }}</v-icon>
          Create Plan
        </v-btn>
      </div>

      <v-data-table :headers="headers" :items="plans.data || []" class="elevation-1" disable-pagination hide-default-footer>
        <template v-slot:body="{ items }">
          <tbody v-if="!loading_data">
            <tr v-if="!items || !items.length">
              <td colspan="10" class="text-center" style="padding: 20px 0px">
                <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
                <p style="font-size: 25px">No Plans Found!!</p>
              </td>
            </tr>
            <tr v-else v-for="item in items" :key="item.id">
              <td>{{ item.id }}</td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span 
                      class="text-truncate d-inline-block" 
                      style="max-width: 200px;"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ (item.name || '') | truncate(30, true) }}
                    </span>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
              </td>
              <td>
                <v-chip small color="info" text-color="white" v-if="item.badge">
                  {{ item.badge }}
                </v-chip>
                <span v-else>-</span>
              </td>
              <td>{{ item.price ? `${item.currency_code || 'USD'} ${item.price}` : 'Free' }}</td>
              <td>{{ item.sync_limit || '-' }}</td>
              <td>{{ item.order_limit || '-' }}</td>
              <td>
                <v-chip small :color="item.status ? 'success' : 'error'" text-color="white">
                  {{ item.status ? 'Active' : 'Inactive' }}
                </v-chip>
              </td>
              <td>
                <v-chip small :color="item.show ? 'primary' : 'grey'" text-color="white">
                  {{ item.show ? 'Visible' : 'Hidden' }}
                </v-chip>
              </td>
              <td>
                <div v-if="item.features && Array.isArray(item.features) && item.features.length > 0">
                  <v-chip 
                    x-small 
                    v-for="(feature, idx) in item.features.slice(0, 2)" 
                    :key="idx"
                    class="ma-1"
                    color="info"
                    text-color="white"
                  >
                    {{ feature }}
                  </v-chip>
                  <span v-if="item.features.length > 2" class="caption grey--text">
                    +{{ item.features.length - 2 }} more
                  </span>
                </div>
                <span v-else class="grey--text">-</span>
              </td>
              <td class="d-flex justify-center align-center">
                <v-btn fab small color="primary" class="mr-2" @click="openEditDialog(item.id)">
                  <v-icon small>{{ icons.mdiPencil }}</v-icon>
                </v-btn>
                <v-btn fab small color="error" @click="openDeleteDialog(item.id)">
                  <v-icon small>{{ icons.mdiDelete }}</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="10" class="text-center" style="padding: 80px 0px">
                <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>

      <v-pagination
        v-model="plans.current_page"
        class="my-4"
        @input="fetchPlans"
        :length="plans.last_page || 1"
        :total-visible="7"
      ></v-pagination>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="create_edit_dialog" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ edit_mode ? 'Edit Plan' : 'Create Plan' }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="planForm">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.title"
                  label="Plan Title *"
                  outlined
                  dense
                  :rules="[rules.required, rules.uniquePlanName]"
                  @input="syncPlanNames"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.code"
                  label="Plan Code"
                  outlined
                  dense
                  type="number"
                  :disabled="true"
                  :value="edit_mode ? plan_data.code : next_plan_code"
                  hint="Auto-generated (starts from 1000)"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.price"
                  label="Price"
                  outlined
                  dense
                  type="number"
                  step="0.01"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.currency_code"
                  label="Currency Code"
                  outlined
                  dense
                  placeholder="USD"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.sync_limit"
                  label="Sync Limit"
                  outlined
                  dense
                  type="number"
                  min="0"
                  @input="syncUploadLimit"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.order_limit"
                  label="Order Limit"
                  outlined
                  dense
                  type="number"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="plan_data.badge"
                  label="Badge"
                  outlined
                  dense
                  placeholder="e.g., Popular"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="subtitle-1">Features List</v-card-title>
                  <v-card-text>
                    <div class="d-flex mb-3">
                      <v-text-field
                        v-model="new_feature"
                        label="Add Feature"
                        outlined
                        dense
                        hide-details
                        @keyup.enter="addFeature"
                        class="mr-2"
                      ></v-text-field>
                      <v-btn color="primary" @click="addFeature" :disabled="!new_feature.trim()">
                        <v-icon left>{{ icons.mdiPlus }}</v-icon>
                        Add
                      </v-btn>
                    </div>
                    <v-list v-if="plan_data.features.length > 0" dense>
                      <v-list-item v-for="(feature, index) in plan_data.features" :key="index">
                        <v-list-item-content>
                          <v-list-item-title>{{ feature }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon small @click="removeFeature(index)">
                            <v-icon small color="error">{{ icons.mdiDelete }}</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <v-alert v-else type="info" dense outlined>
                      No features added yet. Add features using the input above.
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="plan_data.status"
                  label="Active"
                  color="success"
                  hide-details
                  @change="onStatusChange"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="plan_data.show"
                  label="Show on Pricing"
                  color="primary"
                  hide-details
                  :disabled="!plan_data.status"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="plan_data.unified_limit"
                  label="Unified Limit"
                  color="warning"
                  hide-details
                  :disabled="true"
                ></v-switch>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="plan_data.ai_enabled"
                  label="AI Enabled"
                  color="secondary"
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="savePlan" :loading="is_loading">
            {{ edit_mode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="delete_dialog" max-width="500px">
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon color="error" class="mr-2">{{ icons.mdiAlertCircle }}</v-icon>
          Delete Plan
        </v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete this plan?</p>
          <v-alert type="warning" dense outlined class="mt-3">
            This action cannot be undone. If shops are using this plan, deletion will fail.
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="delete_dialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deletePlan" :loading="is_loading">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiPlus, mdiPencil, mdiDelete, mdiClose, mdiAlertCircle, mdiCheck } from '@mdi/js'

export default {
  name: 'CustomPlans',
  data() {
    return {
      loading_data: false,
      is_loading: false,
      edit_mode: false,
      create_edit_dialog: false,
      delete_dialog: false,
      plans: {
        current_page: 1,
        data: [],
        last_page: 1,
      },
      plan_data: {
        name: '',
        code: null,
        price: '',
        currency_code: 'USD',
        sync_limit: '',
        import_upload_limit: '',
        order_limit: '',
        status: true,
        show: true,
        charge_name: '',
        title: '',
        badge: 'Custom',
        features: [],
        unified_limit: true,
        ai_enabled: false,
      },
      next_plan_code: 1000,
      new_feature: '',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Badge', value: 'badge' },
        { text: 'Price', value: 'price' },
        { text: 'Sync Limit', value: 'sync_limit' },
        { text: 'Order Limit', value: 'order_limit' },
        { text: 'Status', value: 'status' },
        { text: 'Visibility', value: 'show' },
        { text: 'Features', value: 'features' },
        { text: 'Actions', value: 'actions' },
      ],
      icons: {
        mdiPlus,
        mdiPencil,
        mdiDelete,
        mdiClose,
        mdiAlertCircle,
        mdiCheck,
      },
      rules: {
        required: value => !!value || 'This field is required',
        uniquePlanName: value => {
          if (!value || !value.trim()) return true
          // Check if plan name already exists (excluding current plan if editing)
          const trimmedValue = value.trim().toLowerCase()
          const existingPlan = (this.plans.data || []).find(p => {
            const planName = (p.name || p.title || '').toLowerCase()
            return planName === trimmedValue && (!this.edit_mode || p.id !== this.plan_data.id)
          })
          return !existingPlan || 'Plan name already exists'
        },
      },
    }
  },
  mounted() {
    this.fetchPlans()
  },
  methods: {
    async fetchPlans() {
      try {
        this.loading_data = true
        const response = await axios.get('/admin/plans', {
          params: {
            page: this.plans.current_page,
          },
        })
        if (response.data && response.data.success) {
          const data = response.data.data
          this.plans = {
            current_page: data.current_page || 1,
            data: Array.isArray(data) ? data : (data.data || []),
            last_page: data.last_page || 1,
          }
          // Get next plan code for new plans
          if (response.data.next_plan_code) {
            this.next_plan_code = response.data.next_plan_code
          }
        }
      } catch (error) {
        console.error('Error fetching plans:', error)
        this.showError('Failed to fetch plans')
      } finally {
        this.loading_data = false
      }
    },
    openCreateDialog() {
      this.edit_mode = false
      this.resetForm()
      this.create_edit_dialog = true
    },
    async openEditDialog(id) {
      try {
        const response = await axios.get(`/admin/plans/${id}`)
        if (response.data && response.data.success) {
          const plan = response.data.data
          this.edit_mode = true
          // Use title as primary, fallback to name if title doesn't exist
          const planTitle = plan.title || plan.name || ''
          this.plan_data = {
            id: plan.id,
            name: planTitle,
            code: plan.code || null,
            price: plan.price || '',
            currency_code: plan.currency_code || 'USD',
            sync_limit: plan.sync_limit || '',
            import_upload_limit: plan.import_upload_limit || plan.sync_limit || '', // Sync with sync_limit
            order_limit: plan.order_limit || '',
            status: plan.status !== undefined ? plan.status : true,
            show: plan.show !== undefined ? plan.show : true,
            charge_name: planTitle,
            title: planTitle,
            badge: plan.badge || 'Custom',
            features: Array.isArray(plan.features) ? plan.features : [],
            unified_limit: plan.unified_limit !== undefined ? plan.unified_limit : true,
            ai_enabled: plan.ai_enabled === true,
          }
          this.create_edit_dialog = true
        }
      } catch (error) {
        console.error('Error fetching plan:', error)
        this.showError('Failed to load plan details')
      }
    },
    openDeleteDialog(id) {
      this.plan_data.id = id
      this.delete_dialog = true
    },
    async savePlan() {
      if (!this.$refs.planForm.validate()) {
        return
      }

      try {
        this.is_loading = true
        // Sync all plan name fields to title
        const planTitle = this.plan_data.title.trim()
        
        const payload = {
          name: planTitle,
          price: this.plan_data.price ? parseFloat(this.plan_data.price) : null,
          currency_code: this.plan_data.currency_code || 'USD',
          sync_limit: this.plan_data.sync_limit ? parseInt(this.plan_data.sync_limit) : null,
          import_upload_limit: this.plan_data.sync_limit ? parseInt(this.plan_data.sync_limit) : null, // Same as sync_limit
          order_limit: this.plan_data.order_limit ? parseInt(this.plan_data.order_limit) : null,
          status: this.plan_data.status,
          show: this.plan_data.show,
          charge_name: planTitle, // Same as title
          title: planTitle, // Primary field
          badge: this.plan_data.badge || 'Custom',
          features: this.plan_data.features, // Send as array
          unified_limit: true, // Always true
          is_unlimited: false, // Always false
          is_highest_plan: false, // Always false
          ai_enabled: this.plan_data.ai_enabled === true,
        }

        // Only include code if editing (though it won't be used)
        if (this.edit_mode) {
          payload.code = this.plan_data.code
        }

        if (this.edit_mode) {
          await axios.put(`/admin/plans/${this.plan_data.id}`, payload)
          this.showSuccess('Plan updated successfully')
        } else {
          await axios.post('/admin/plans', payload)
          this.showSuccess('Plan created successfully')
        }
        this.closeDialog()
        this.fetchPlans()
      } catch (error) {
        console.error('Error saving plan:', error)
        const errorMsg = error.response?.data?.message || 'Failed to save plan'
        const errors = error.response?.data?.errors
        if (errors) {
          const errorText = Object.values(errors).flat().join(', ')
          this.showError(`${errorMsg}: ${errorText}`)
        } else {
          this.showError(errorMsg)
        }
      } finally {
        this.is_loading = false
      }
    },
    async deletePlan() {
      try {
        this.is_loading = true
        await axios.delete(`/admin/plans/${this.plan_data.id}`)
        this.showSuccess('Plan deleted successfully')
        this.delete_dialog = false
        this.fetchPlans()
      } catch (error) {
        console.error('Error deleting plan:', error)
        const errorMsg = error.response?.data?.message || 'Failed to delete plan'
        this.showError(errorMsg)
      } finally {
        this.is_loading = false
      }
    },
    closeDialog() {
      this.create_edit_dialog = false
      this.resetForm()
    },
    resetForm() {
      this.plan_data = {
        name: '',
        code: null,
        price: '',
        currency_code: 'USD',
        sync_limit: '',
        import_upload_limit: '',
        order_limit: '',
        status: true,
        show: true,
        charge_name: '',
        title: '',
        badge: 'Custom',
        features: [],
        unified_limit: true,
        ai_enabled: false,
      }
      this.new_feature = ''
      this.edit_mode = false
      if (this.$refs.planForm) {
        this.$refs.planForm.resetValidation()
      }
    },
    syncPlanNames() {
      // Sync name, charge_name, and title to be the same
      const titleValue = this.plan_data.title
      this.plan_data.name = titleValue
      this.plan_data.charge_name = titleValue
    },
    syncUploadLimit() {
      // Sync import_upload_limit to be same as sync_limit
      this.plan_data.import_upload_limit = this.plan_data.sync_limit
    },
    addFeature() {
      if (this.new_feature.trim()) {
        this.plan_data.features.push(this.new_feature.trim())
        this.new_feature = ''
      }
    },
    removeFeature(index) {
      this.plan_data.features.splice(index, 1)
    },
    onStatusChange(value) {
      // If status is active, show should also be true
      if (value) {
        this.plan_data.show = true
      }
    },
    showSuccess(message) {
      this.$swal.fire({
        icon: 'success',
        title: 'Success',
        text: message,
        toast: true,
        position: 'bottom-end',
        timer: 3000,
        showConfirmButton: false,
      })
    },
    showError(message) {
      this.$swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
        toast: true,
        position: 'bottom-end',
        timer: 5000,
        showConfirmButton: false,
      })
    },
  },
}
</script>
